import { Dialog } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { LearningTrailsService } from '@app/core';
import { notNull } from '@app/shared/utils';
import { SIDENAV_START, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { addMinutes } from 'date-fns';
import { isEmpty, isEqual, toInteger } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  exhaustMap,
  filter,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ModalSubdivisionsComponent } from '../../components/modal-subdivisions/modal-subdivisions.component';
import { SidenavLearningTrailsComponent } from '../../components/sidenav-learning-trails/sidenav-learning-trails.component';
import { Collection, Course } from '../../models';
import { TrailsService } from '../../services';

@Component({
  selector: 'app-showcase',
  templateUrl: './courses.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CoursesComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dialog = inject(Dialog);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly trailsService = inject(TrailsService);
  private readonly store = inject(Store<AppState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly learningTrailsService = inject(LearningTrailsService);
  private readonly storageService = inject(LocalStorageService);
  private readonly sidenavData = inject<Observable<SidenavRef>>(SIDENAV_START);
  private readonly _activatedRoute = inject(ActivatedRoute);

  public collectionIndexToOpened: number;
  public voucherEnabled: number;

  public courseSelected$ = new BehaviorSubject<Course>(undefined);
  public collections$ = new BehaviorSubject<Collection[]>(undefined);
  public collectionsSelected = signal([]);
  public coursesSelected = signal([]);
  public loading$ = new Subject<boolean>();

  private readonly COLLECTION_KEY = 'trail-collection';
  private readonly COURSE_KEY = 'trail-course';
  private readonly COURSE_DATE_KEY = 'trail-course-date';

  public ngOnInit(): void {
    this.sidenav.pipe(take(1)).subscribe((sidenav) => {
      sidenav.collections$.next([]);
      sidenav.search.setValue(this._activatedRoute.snapshot.queryParams.search);
    });

    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        notNull(),
        tap(() => this.loading$.next(true)),
        switchMap(() => this.learningTrailsService.courses()),
        tap(({ data }) => this.collections$.next(data)),
        tap(() => this.loading$.next(false)),
        tap(({ data }) => {
          const courses = [];
          const collections = [];
          data.forEach((collection) => {
            collection.cursos.forEach((course) => {
              collections.push(collection.id);
              courses.push(course);
            });
          });
          this.collectionsSelected.set(collections);
          this.coursesSelected.set(courses.map((course) => course.id));

          if (!isEmpty(this.route.snapshot.queryParams)) {
            const { courseSlug, eventId } = {
              courseSlug: this.route.snapshot.queryParams.courseSlug,
              eventId: toInteger(this.route.snapshot.queryParams.eventId),
            };
            const course = courses.find((course) => course.slug === courseSlug && course.evento?.id === eventId);
            if (!!course) this.setCourseSelected(course);
            this.router.navigate([], {
              queryParams: {},
              replaceUrl: true,
            });
          }
        }),
        switchMap(({ data }) =>
          this.sidenav.pipe(
            take(1),
            tap((sidenav) => sidenav.collections$.next(data)),
          ),
        ),
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    this.sidenav
      .pipe(switchMap((sidenav) => sidenav.collectionsSelected$ as Observable<number[]>))
      .subscribe((collectionsSelected) => this.collectionsSelected.set(collectionsSelected));

    this.sidenav
      .pipe(switchMap((sidenav) => sidenav.coursesSelected$ as Observable<number[]>))
      .subscribe((coursesSelected) => this.coursesSelected.set(coursesSelected));

    this.collections$.pipe(notNull(), take(1)).subscribe((collections) => {
      const collectionIndex = collections.findIndex(
        (collection) => collection.id === this.storageService.retrieve(this.COLLECTION_KEY),
      );
      const courseId = this.storageService.retrieve(this.COURSE_KEY);
      const validateTime = addMinutes(new Date(this.storageService.retrieve(this.COURSE_DATE_KEY)), 10) > new Date();

      if (validateTime && courseId && collections[collectionIndex].cursos.some((course) => course.id === courseId)) {
        const course = collections[collectionIndex].cursos.find((course) => course.id === courseId);
        this.setCourseSelected(course, collections[collectionIndex].id);
      }
    });
  }

  public ngOnDestroy(): void {
    this.collections$.complete();
    this.loading$.complete();
    this.courseSelected$.complete();
  }

  public setCollectionIndexToOpened(value: number): void {
    this.collectionIndexToOpened = value;
  }

  public OnVoucherEnabled(value: number): void {
    this.voucherEnabled = value;

    const courses = this.collections$.value.map((collection) => ({
      ...collection,
      cursos: collection.cursos.map((course) => ({
        ...course,
        enable: course.produtos.some((product) => product.id === value),
      })),
    }));

    this.collections$.next(courses);
  }

  public setCourseSelected(course: Course, collectionId?: number): void {
    if (!!collectionId) {
      this.storageService.store(this.COLLECTION_KEY, collectionId);
      this.storageService.store(this.COURSE_KEY, course.id);
      this.storageService.store(this.COURSE_DATE_KEY, new Date());
    }
    this.dialog
      .open(ModalSubdivisionsComponent, { data: { course, isShowcase: false } })
      .closed.subscribe((result: any) => {
        if (!result?.reset) {
          this.storageService.clear(this.COURSE_KEY);
        }
      });
    this.courseSelected$.next(course);
  }

  public filtered(id: number, selected: number[]): boolean {
    return selected.includes(id);
  }

  public onOpenDashboard() {
    this.router.navigateByUrl(`/${this.getUrlUcIvca()}/dashboard`);
  }

  private get sidenav() {
    return this.sidenavData.pipe(
      notNull(),
      takeUntilDestroyed(this.destroyRef),
      exhaustMap((res) => res.componentInstance$),
      notNull(),
      distinctUntilChanged(isEqual),
      filter((res) => res instanceof SidenavLearningTrailsComponent),
    );
  }

  private getUrlUcIvca(): string {
    if (this.trailsService.uc$.value) return 'universidade-corporativa';
    if (this.trailsService.ivca$.value) return 'ivca';
    return 'trilhas-de-aprendizagem';
  }
}
